// Winback SİTESİ kabuğu (sahip 2026-10-07): ayrı adres (winback.151.243.224.168.sslip.io), kendi
// kullanıcıları, iki sekme — Winback (panelle AYNI sayfa bileşeni) ve Ayarlar (şifre + kullanıcılar).
// Oturumun otoritesi httpOnly `wb_sid` çerezidir; açılışta /api/auth/me ile doğrulanır.
// Sekme değişince Winback sayfası SÖKÜLMEZ (gizlenir) — yapılmış analiz kaybolmasın.

import { useEffect, useRef, useState } from 'react';
import { useTranslateDOM } from '../i18n';
import Login from '../pages/Login';
import Winback from '../pages/Winback';
import { Icon, Skeleton } from '../ui';
import type { BrandId, Lang, PanelUser } from '../types';
import { siteLogout, siteMe, type SiteUser } from './api';
import SiteSettings from './SiteSettings';

type Tab = 'winback' | 'settings';

const LS_LANG = 'wb.lang';
const LS_TAB = 'wb.tab';

const readLS = (k: string, d: string): string => {
  try {
    return localStorage.getItem(k) ?? d;
  } catch {
    return d;
  }
};
const writeLS = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* özel mod — bellekte devam */
  }
};

const vars: React.CSSProperties = {
  ['--bg' as string]: '#0d1117',
  ['--surface' as string]: '#161b22',
  ['--surface2' as string]: '#1b2230',
  ['--border' as string]: '#242b36',
  ['--border2' as string]: '#2d3543',
  ['--text' as string]: '#e6edf3',
  ['--dim' as string]: '#9aa4b2',
  ['--faint' as string]: '#6b7280',
  ['--accent' as string]: '#3d8bff',
  ['--accent-soft' as string]: 'rgba(61,139,255,.13)',
  ['--accent-border' as string]: 'rgba(61,139,255,.45)',
  ['--good' as string]: '#37c46b',
  ['--warn' as string]: '#e0a23c',
  ['--bad' as string]: '#ef5d5d',
  minHeight: '100vh',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontFamily: "'Inter',system-ui,-apple-system,sans-serif",
  WebkitFontSmoothing: 'antialiased',
};

const tabStyle = (on: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  padding: '8px 14px',
  borderRadius: '9px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid ' + (on ? 'var(--accent-border)' : 'transparent'),
  background: on ? 'var(--accent-soft)' : 'transparent',
  color: on ? '#e6edf3' : '#9aa4b2',
});

const langBtn = (on: boolean): React.CSSProperties => ({
  padding: '5px 11px', fontSize: '11.5px', fontWeight: 700, border: 'none', borderRadius: '7px', cursor: 'pointer',
  background: on ? 'var(--accent-soft)' : 'transparent', color: on ? '#e6edf3' : '#6b7280',
});

export default function SiteApp() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<SiteUser | null>(null);
  const [checked, setChecked] = useState(false);
  const [lang, setLangState] = useState<Lang>(() => (readLS(LS_LANG, 'tr') === 'en' ? 'en' : 'tr'));
  const [tab, setTabState] = useState<Tab>(() => (readLS(LS_TAB, 'winback') === 'settings' ? 'settings' : 'winback'));

  useTranslateDOM(lang, rootRef);

  useEffect(() => {
    document.title = 'Winback';
    let alive = true;
    siteMe()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); })
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, []);

  // Herhangi bir istekte 401 → oturumu düşür (../api ve ./api aynı olayı yayınlar).
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener('caa:unauthorized', onUnauthorized);
    return () => window.removeEventListener('caa:unauthorized', onUnauthorized);
  }, []);

  const setLang = (l: Lang) => { writeLS(LS_LANG, l); setLangState(l); };
  const setTab = (t: Tab) => { writeLS(LS_TAB, t); setTabState(t); };
  const logout = () => { void siteLogout(); setUser(null); setTab('winback'); };

  return (
    <div key={'lang-' + lang} ref={rootRef} style={vars}>
      {!checked ? (
        <div style={{ padding: '32px' }}><Skeleton heights={[56, 160]} /></div>
      ) : !user ? (
        <Login
          showDemo={false}
          title="Winback"
          subtitle="Müşteri ID ile yatırım, bonus ve oyun analizi"
          icon="person_search"
          // Login ortak bileşen PanelUser tipiyle döner; site /api/auth/login gövdesi SiteUser'dır.
          onSuccess={(u: PanelUser) => { setUser(u as unknown as SiteUser); setTab('winback'); }}
        />
      ) : (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <header style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px clamp(16px,3vw,28px)', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <Icon name="person_search" size={21} color="#fff" />
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em' }}>Winback</div>
            </div>
            <nav style={{ display: 'flex', gap: '6px', marginLeft: '10px' }}>
              <button type="button" onClick={() => setTab('winback')} style={tabStyle(tab === 'winback')}>
                <Icon name="person_search" size={17} />
                <span>Winback</span>
              </button>
              <button type="button" onClick={() => setTab('settings')} style={tabStyle(tab === 'settings')}>
                <Icon name="settings" size={17} />
                <span>Ayarlar</span>
              </button>
            </nav>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '9px' }}>
                <button onClick={() => setLang('tr')} style={langBtn(lang === 'tr')}>TR</button>
                <button onClick={() => setLang('en')} style={langBtn(lang === 'en')}>EN</button>
              </div>
              <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{user.displayName}</span>
                <span style={{ fontSize: '11px', color: 'var(--faint)' }}>{user.roleLabel}</span>
              </span>
              <button
                onClick={logout}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 11px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: '#ef8a8a', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer' }}
              >
                <Icon name="logout" size={17} />
                <span>Çıkış</span>
              </button>
            </div>
          </header>
          <main style={{ flex: 1, padding: '24px clamp(16px,3vw,28px)' }}>
            <div style={{ display: tab === 'winback' ? 'block' : 'none' }}>
              <Winback brands={user.brands as BrandId[]} lang={lang} standalone />
            </div>
            {tab === 'settings' && <SiteSettings user={user} />}
          </main>
        </div>
      )}
    </div>
  );
}
